/**
 * @file blendBrief.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-09-17
 * @description Turn "18% protein, 5% fibre, 20% starch, use Rolled Wheat and
 *              Propass" into a blend.
 *
 *              This is how the request actually arrives — spoken down the phone,
 *              or written on the back of a delivery note. The calculator made
 *              you translate it: work out which materials carry protein, guess
 *              a starting split, then chase the numbers around until the total
 *              came back to 100. The translation is the boring part and it is
 *              the part a machine is good at.
 *
 *              The parse is deliberately plain — regexes over a nutrient
 *              vocabulary and the material list, nothing learned and nothing
 *              remote. That matters more than cleverness would: it always gives
 *              the same answer for the same words, it can show you exactly what
 *              it understood before it acts, and it cannot invent a constraint
 *              that was never asked for. Anything it fails to read comes back in
 *              `unmatched` rather than being quietly dropped.
 *
 *              What it produces is a spec, and the spec goes to the same LP
 *              everything else uses. So the suggestions are not guesses that
 *              look plausible — each one is a proved least-cost answer under a
 *              different reading of the request, and the readings are the thing
 *              worth choosing between: hold the numbers exactly, or let them
 *              float and see what it saves.
 *
 *              RULES ABOUT THE BLEND AS A WHOLE (1.3.0)
 *
 *              "Under £250 per tonne", "minimum 4 ingredients maximum 7" and
 *              "three variations" are not about any one material or nutrient, and
 *              used to be read as nothing at all — the first two quietly ignored,
 *              the third only by the AI reader. The brief now carries costMax,
 *              minIngredients and maxIngredients beside optionCount, both readers
 *              fill them, and every solve keeps them: the ingredient counts and
 *              the variations through the exact search in blendSearch.js, the
 *              ceiling as one more linear row. An answer that cannot keep them is
 *              not shown; the refusal says which rule could not be met.
 *
 *              MINIMUM INCLUSION IS ASKED FOR, NEVER ASSUMED (1.4.0)
 *
 *              There is no default minimum any more — see blendInclusion.js. The
 *              brief carries `minInclusion` only when the request states one:
 *              "each ingredient at least 1%", "no ingredient under 0.5%",
 *              "minimum inclusion 2%". Both readers fill it, and every solve
 *              keeps it for every material in the answer.
 */

// Explicit extensions: webpack does not need them, but
// scripts/check-blend-solver.mjs imports this straight into bare node, which does.
import { solveBlend, solveClosestToBase } from './blendSolver.js';
import {
  countIngredients, isIncluded, formatInclusion, INCLUSION_DECIMALS, MAX_MIN_INCLUSION
} from './blendInclusion.js';
import * as blendMath from './blendMath.js';

/** Inclusions are rounded to two decimals, so a stated figure is met to within half a step. */
const HALF_STEP = 0.5 / 10 ** INCLUSION_DECIMALS;

/**
 * What people call each nutrient.
 *
 * Keyed by the component key the book uses. "cp" and "protein" are the same
 * request; so are "fat" and "oil". Longest alias first when matching, or "me"
 * would match inside "meal".
 */
const NUTRIENT_WORDS = {
  protein_pct: ['crude protein', 'protein', 'cp'],
  fibre_pct: ['crude fibre', 'crude fiber', 'fibre', 'fiber', 'cf'],
  starch: ['starch'],
  oil_b_pct: ['oil b', 'oil', 'fat'],
  ash_pct: ['ash'],
  dm_pct: ['dry matter', 'dm'],
  me_mj_kg: ['metabolisable energy', 'energy', 'me'],
  dgndf: ['dgndf', 'ndf'],
  dup_fim: ['dup']
};

const MAX_WORDS = ['max', 'maximum', 'no more than', 'not more than', 'under', 'below', 'up to', 'at most',
  'less than', 'fewer than', '<=', '≤', '<'];
const MIN_WORDS = ['min', 'minimum', 'at least', 'no less than', 'over', 'above',
  'more than', 'greater than', 'no fewer than', 'not less than', '>=', '≥', '>'];

/**
 * Qualifiers that exclude the figure they name. Irrelevant to a percentage —
 * "< 50% wheatfeed" and "at most 50%" are the same limit to a continuous solver —
 * but not to a count: "fewer than 8 ingredients" is at most 7.
 */
const STRICT_WORDS = ['under', 'below', 'less than', 'fewer than', '<', 'over', 'above', 'more than',
  'greater than', '>'];

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * A qualifier alternation for a RegExp: words at a word boundary, symbols
 * anywhere, longest first so "<=" is not read as "<" followed by "=".
 */
const qualifierPattern = (words) => [...words]
  .sort((a, b) => b.length - a.length)
  .map((w) => (/^[a-z]/i.test(w) ? `\\b${escapeRe(w).replace(/ /g, '\\s+')}` : escapeRe(w)))
  .join('|');

/** 'max' | 'min' | null, for a qualifier as it was typed. */
const qualifierKind = (q) => {
  const t = String(q || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (MAX_WORDS.includes(t)) return 'max';
  if (MIN_WORDS.includes(t)) return 'min';
  return null;
};

const normalise = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Counts are said in words as often as figures: "three variations". */
const NUMBER_WORDS = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8,
  nine: 9, ten: 10, eleven: 11, twelve: 12, fifteen: 15, twenty: 20
};
const COUNT = `(\\d+|${Object.keys(NUMBER_WORDS).join('|')})`;
const countOf = (s) => (/^\d+$/.test(String(s)) ? parseInt(s, 10) : NUMBER_WORDS[String(s).toLowerCase()]);

/**
 * What an ingredient is called when it is being counted. Loose on purpose —
 * "ingrediants" and "ingredents" are how it gets typed on a phone.
 */
const INGREDIENT = '(?:raw\\s+)?(?:in?gr?e?d\\w*|materials?|components?|constituents?)';

/**
 * Words meaning "alternatives", matched with a little give for spelling:
 * "three varations" is a request for three variations, and a parser that needs
 * it spelled correctly has failed the person typing it.
 */
const VARIATION_WORDS = ['variations', 'variation', 'variants', 'variant', 'options', 'option',
  'alternatives', 'alternative', 'versions', 'version', 'choices', 'recipes', 'formulations',
  'blends', 'suggestions'];

function editDistance(a, b) {
  const row = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const held = row[j];
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = held;
    }
  }
  return row[b.length];
}

export function isVariationWord(word) {
  const w = String(word || '').toLowerCase();
  if (w.length < 4) return false;
  return VARIATION_WORDS.some((target) =>
    w === target || editDistance(w, target) <= (target.length >= 8 ? 2 : 1));
}

/**
 * Match a phrase to a material.
 *
 * Token containment rather than a similarity score: "propass" should find
 * "PROPASS (North American)" and "rolled wheat" should find "ROLLED WHEAT"
 * without also matching "WHEAT GLUTEN" more strongly. A phrase whose tokens are
 * all present in the name is a hit, ranked by how little else the name carries.
 */
function matchMaterial(phrase, materials) {
  const wanted = normalise(phrase).split(' ').filter(Boolean);
  if (!wanted.length) return null;

  let best = null;
  for (const m of materials) {
    const name = normalise(m.name);
    const tokens = name.split(' ').filter(Boolean);
    if (!wanted.every((w) => tokens.some((t) => t === w || t.startsWith(w)))) continue;
    // Fewest extra words wins: an exact name beats one with a bracketed suffix.
    const slack = tokens.length - wanted.length;
    if (!best || slack < best.slack) best = { material: m, slack };
  }
  return best?.material || null;
}

/**
 * Find the thing a brief is talking about — a saved blend, a formulation, or a
 * customer.
 *
 * "take Chapple's Nov blend and make it 18% protein" names a starting point,
 * and without recognising it the request reads as "build me an 18% protein
 * blend from nothing", which is a different job with a different answer.
 *
 * Scored rather than matched: names in this system are long, inconsistently
 * punctuated and often contain a customer as well as a product ("CF Nov 25
 * Blend (Treleaven)"). An exact hit is rare; what is common is most of the
 * distinctive words appearing somewhere in the sentence. So every candidate is
 * scored on how much of ITS name the brief accounts for, and the caller is shown
 * the runners-up rather than being given one answer to trust.
 *
 * Single-word names are required to match whole: "blend" appearing in every
 * name would otherwise match every one of them.
 */
const STOP_WORDS = new Set([
  'take', 'the', 'a', 'an', 'and', 'make', 'it', 'to', 'of', 'for', 'with',
  'use', 'using', 'blend', 'formulation', 'please', 'can', 'you', 'me', 'my',
  'that', 'this', 'from', 'at', 'in', 'on', 'up', 'but', 'change', 'adjust',
  'like', 'same', 'as', 'is', 'be', 'get', 'new', 'version', 'copy'
]);

/**
 * Low enough that a customer's name pulls in their blends alongside anything
 * that carries the name directly.
 *
 * "The Treleaven blend" matches a formulation literally called "... (Treleaven)"
 * more strongly than a blend merely BELONGING to Treleaven Farms, and both are
 * reasonable answers. A tighter floor picks one and hides the other; this floor
 * offers both and lets the person who asked say which they meant — which is the
 * only reliable way to settle it.
 */
const CANDIDATE_FLOOR = 0.45;

function scoreName(text, name) {
  const words = normalise(name).split(' ').filter((w) => w && !STOP_WORDS.has(w));
  if (!words.length) return 0;
  const haystack = ` ${normalise(text)} `;
  let hits = 0;
  let weight = 0;
  for (const w of words) {
    // Longer words are more distinctive: matching "treleaven" says far more
    // than matching "25".
    const value = Math.max(1, w.length);
    weight += value;
    if (haystack.includes(` ${w} `) || haystack.includes(` ${w}`)) hits += value;
  }
  const share = hits / weight;
  // A single distinctive word is enough; a single common one is not.
  if (words.length === 1 && share < 1) return 0;
  return share;
}

/**
 * @param {string} text
 * @param {Object} catalogue { blends, formulations, customers }
 * @returns {{base, baseCandidates, customer}}
 */
export function findReferences(text, catalogue = {}) {
  const scored = [];

  for (const b of catalogue.blends || []) {
    const score = Math.max(
      scoreName(text, b.name),
      // A customer's name in the sentence points at their blends, but less
      // strongly than the blend's own name does.
      b.customer_name ? scoreName(text, b.customer_name) * 0.75 : 0
    );
    if (score >= CANDIDATE_FLOOR) {
      scored.push({ kind: 'blend', id: b.id, name: b.name, sub: b.customer_name || '', score });
    }
  }

  for (const f of catalogue.formulations || []) {
    const score = Math.max(
      scoreName(text, f.item_name),
      f.item_code ? scoreName(text, f.item_code) : 0,
      f.customer_blend_name ? scoreName(text, f.customer_blend_name) * 0.75 : 0
    );
    if (score >= CANDIDATE_FLOOR) {
      scored.push({
        kind: 'formulation', id: f.id,
        name: f.item_name, sub: f.item_code || '', score
      });
    }
  }

  scored.sort((a, b) => b.score - a.score || String(a.name).localeCompare(String(b.name)));

  // A customer named without a specific product is a filter on the candidates
  // rather than an answer in itself.
  let customer = null;
  for (const c of catalogue.customers || []) {
    const score = scoreName(text, c.company_name || c.name || '');
    if (score >= 0.8) { customer = { id: c.id, name: c.company_name || c.name, score }; break; }
  }

  return {
    // Only claim a base when one clearly leads. Two names scoring the same is
    // an ambiguity to hand back, not a coin to toss.
    base: scored.length && (scored.length === 1 || scored[0].score > scored[1].score + 0.001)
      ? scored[0] : null,
    baseCandidates: scored.slice(0, 8),
    customer
  };
}

/**
 * Read the rules about the blend as a whole: what it may cost, how many
 * ingredients it has, and how many different answers were asked for.
 *
 *   "Under £250 per tonne"             → costMax 250
 *   "minimum 4 ingredients maximum 7"  → minIngredients 4, maxIngredients 7
 *   "each ingredient at least 1%"      → minInclusion 1
 *   "three varations"                  → optionCount 3
 *
 * A figure only counts as a cost when it carries a £ or a "per tonne", only as a
 * count when it names ingredients (or follows one that did), and only as a
 * minimum inclusion when it is a percentage said of EVERY ingredient — "under
 * 50% wheatfeed" is none of them, and must stay a limit on wheatfeed.
 *
 * @param {string} text
 * @returns {{costMax, minIngredients, maxIngredients, minInclusion, optionCount, spans:number[][]}}
 *          spans are the character ranges read, so nothing else reads them again.
 */
export function parseRules(text) {
  const raw = String(text || '');
  const spans = [];
  const taken = (index) => spans.some(([s, e]) => index >= s && index < e);
  const take = (start, length) => spans.push([start, start + length]);
  const out = {
    costMax: null, minIngredients: null, maxIngredients: null, minInclusion: null, optionCount: null, spans
  };

  // ── Cost ceiling ──────────────────────────────────────────────────────────
  const money = '(\\d[\\d,]*(?:\\.\\d+)?)(?![\\d.,])';
  const perTonne = '(?:\\s*(?:\\/|per|a)\\s*(?:t|tonnes?|tons?|mt)\\b)';
  const ceilingWords = qualifierPattern([...MAX_WORDS, 'within', 'budget of', 'budget', 'cheaper than', 'no dearer than']);
  const costPatterns = [
    // "under £250 per tonne", "< £250/t", "no more than £250"
    new RegExp(`(?:${ceilingWords})\\s*(?:a\\s+)?(?:cost\\s+of\\s+|price\\s+of\\s+)?£\\s*${money}${perTonne}?`, 'i'),
    // "under 250 per tonne"
    new RegExp(`(?:${ceilingWords})\\s*${money}\\s*(?:£|gbp|pounds?)?${perTonne}`, 'i'),
    // "£250/t max", "£250 a tonne or less"
    new RegExp(`£\\s*${money}${perTonne}?\\s*(?:max|maximum|or\\s+less|or\\s+under|tops|ceiling|budget)\\b`, 'i'),
    // "cost under 250", "budget £250"
    new RegExp(`\\b(?:cost|price|budget)\\s*(?:of|:|=|is|at)?\\s*(?:${ceilingWords})?\\s*£?\\s*${money}(?!\\s*%)`, 'i')
  ];
  for (const re of costPatterns) {
    const m = re.exec(raw);
    if (!m) continue;
    const value = parseFloat(m[1].replace(/,/g, ''));
    if (!Number.isFinite(value) || value <= 0) continue;
    out.costMax = value;
    take(m.index, m[0].length);
    break;
  }

  // ── Minimum inclusion ─────────────────────────────────────────────────────
  // Before the counts, so "ingredients at least 1% each" is a minimum rather
  // than something a count reader might start on. Every pattern needs the
  // figure said of every ingredient — "each", "no ingredient", "inclusion" — so
  // "minimum 2% molasses" stays a limit on molasses.
  const pct = '(\\d+(?:\\.\\d+)?|\\.\\d+)\\s*(?:%|per\\s*cent\\b)';
  const pctMaybe = '(\\d+(?:\\.\\d+)?|\\.\\d+)(?![\\d.])\\s*(?:%|per\\s*cent\\b)?';
  const atLeast = '(?:\\b(?:at\\s+least|min(?:imum)?|no\\s+less\\s+than|not\\s+less\\s+than)|>=|≥|>)';
  const below = '(?:\\b(?:under|below|less\\s+than|lower\\s+than)|<)';
  const every = '(?:each|every|all|any)\\s+(?:of\\s+the\\s+)?(?:single\\s+)?';
  const linking = '(?:(?:must|should|has\\s+to|have\\s+to|needs?\\s+to)\\s+be\\s+|is\\s+|are\\s+|to\\s+be\\s+|of\\s+|at\\s+|(?:included|used)\\s+at\\s+)?';
  const ends = '(?=\\s*(?:[,.;&]|\\band\\b|$))';
  const minInclusionPatterns = [
    // "each ingredient at least 1%", "all ingredients must be at least 0.5%"
    new RegExp(`\\b${every}${INGREDIENT}\\s+${linking}${atLeast}\\s*${pct}`, 'i'),
    // "each ingredient 1% minimum", "every material 0.5% or more"
    new RegExp(`\\b${every}${INGREDIENT}\\s+(?:at\\s+)?${pct}\\s*(?:min(?:imum)?|or\\s+more|at\\s+least)\\b`, 'i'),
    // "no ingredient under 0.5%", "no single material below 1%"
    new RegExp(`\\bno\\s+(?:single\\s+)?${INGREDIENT}\\s+${linking}${below}\\s*${pct}`, 'i'),
    // "nothing under 1%" — only when it ends there, or it is a limit on something
    new RegExp(`\\bnothing\\s+${linking}${below}\\s*${pct}${ends}`, 'i'),
    // "minimum inclusion 2%", "min inclusion rate of 0.5%"
    new RegExp(`\\bmin(?:imum)?\\s+inclusions?(?:\\s+(?:rate|level)s?)?\\s*(?:of|is|=|:|at)?\\s*${pctMaybe}${ends}`, 'i'),
    // "inclusion at least 1%", "inclusions of no less than 0.5%"
    new RegExp(`\\binclusions?(?:\\s+(?:rate|level)s?)?\\s+${linking}${atLeast}\\s*${pct}`, 'i'),
    // "at least 1% of each ingredient", "minimum 0.5% per material"
    new RegExp(`${atLeast}\\s*${pct}\\s*(?:of\\s+|for\\s+)?(?:each|every|per|any)\\s+${INGREDIENT}`, 'i'),
    // "ingredients at least 1% each", "minimum 4 ingredients, each at least 1%"
    new RegExp(`\\b(?:${INGREDIENT}|each)\\s+${linking}${atLeast}\\s*${pct}(?:\\s*each\\b|${ends})`, 'i')
  ];
  for (const re of minInclusionPatterns) {
    const m = re.exec(raw);
    if (!m) continue;
    const value = parseFloat(m[1]);
    if (!Number.isFinite(value) || value <= 0 || value > MAX_MIN_INCLUSION) continue;
    out.minInclusion = value;
    take(m.index, m[0].length);
    break;
  }

  // ── Ingredient counts ─────────────────────────────────────────────────────
  const minWords = qualifierPattern(MIN_WORDS);
  const maxWords = qualifierPattern(MAX_WORDS);
  const either = `${minWords}|${maxWords}`;

  /** A qualified count, adjusted for strictness: "fewer than 8" is at most 7. */
  const apply = (qualifier, n) => {
    const kind = qualifierKind(qualifier);
    if (!kind || !Number.isInteger(n) || n <= 0) return false;
    const strict = STRICT_WORDS.includes(String(qualifier).toLowerCase().replace(/\s+/g, ' ').trim());
    if (kind === 'min') out.minIngredients = strict ? n + 1 : n;
    else out.maxIngredients = strict ? n - 1 : n;
    return true;
  };

  // "minimum 4 ingredients maximum 7" — the second figure borrows the first's
  // unit. Read either way round, so "max 7, min 4 ingredients" works too.
  const bareCount = `${COUNT}(?![\\d.])(?!\\s*%)(?!\\s*£)`;
  const joiner = '\\s*(?:,|&|\\band\\b|\\bbut\\b)?\\s*';
  const readNeighbours = (start, end) => {
    const after = new RegExp(`^${joiner}(${either})\\s*(?:of\\s+)?${bareCount}(?:\\s+${INGREDIENT}\\b)?`, 'i')
      .exec(raw.slice(end));
    if (after && apply(after[1], countOf(after[2]))) take(end, after[0].length);
    const before = new RegExp(`(${either})\\s*(?:of\\s+)?${bareCount}${joiner}$`, 'i')
      .exec(raw.slice(0, start));
    if (before && apply(before[1], countOf(before[2]))) take(before.index, before[0].length);
  };

  const range = new RegExp(`\\bbetween\\s+${COUNT}\\s+(?:and|-|to|–)\\s+${COUNT}\\s+${INGREDIENT}\\b`, 'i').exec(raw)
    || new RegExp(`\\b${COUNT}\\s*(?:-|–|to)\\s*${COUNT}\\s+${INGREDIENT}\\b`, 'i').exec(raw);
  if (range) {
    const a = countOf(range[1]);
    const b = countOf(range[2]);
    out.minIngredients = Math.min(a, b);
    out.maxIngredients = Math.max(a, b);
    take(range.index, range[0].length);
  }

  for (const m of raw.matchAll(new RegExp(`(${either})\\s*(?:of\\s+)?${COUNT}\\s+${INGREDIENT}\\b`, 'gi'))) {
    if (taken(m.index)) continue;
    if (!apply(m[1], countOf(m[2]))) continue;
    take(m.index, m[0].length);
    readNeighbours(m.index, m.index + m[0].length);
  }

  // "4+ ingredients", "4 ingredients or more", "7 ingredients max".
  for (const m of raw.matchAll(new RegExp(`\\b${COUNT}\\s*(?:\\+|or\\s+more|plus)\\s*${INGREDIENT}\\b|\\b${COUNT}\\s+${INGREDIENT}\\s+(or\\s+more|minimum|min|at\\s+least|or\\s+fewer|or\\s+less|maximum|max|at\\s+most|tops)\\b`, 'gi'))) {
    if (taken(m.index)) continue;
    if (m[1]) {
      out.minIngredients = countOf(m[1]);
    } else {
      const n = countOf(m[2]);
      if (/more|min|least/i.test(m[3])) out.minIngredients = n;
      else out.maxIngredients = n;
    }
    take(m.index, m[0].length);
  }

  // A bare "4 ingredients" is exactly four — but only when nothing more
  // specific was said.
  for (const m of raw.matchAll(new RegExp(`\\b${COUNT}[\\s-]+${INGREDIENT}\\b`, 'gi'))) {
    if (taken(m.index)) continue;
    if (out.minIngredients !== null || out.maxIngredients !== null) break;
    const n = countOf(m[1]);
    out.minIngredients = n;
    out.maxIngredients = n;
    take(m.index, m[0].length);
    break;
  }

  // ── How many answers ──────────────────────────────────────────────────────
  // Only with a number: "some alternatives" is not a count, and the AI reader is
  // told the same.
  const variationRe = new RegExp(`\\b${COUNT}\\s+(?:different\\s+|alternative\\s+|possible\\s+|more\\s+|other\\s+|blend\\s+)?([a-z]+)\\b`, 'gi');
  for (const m of raw.matchAll(variationRe)) {
    if (taken(m.index) || !isVariationWord(m[2])) continue;
    const n = countOf(m[1]);
    if (!Number.isInteger(n) || n <= 0) continue;
    out.optionCount = n;
    take(m.index, m[0].length);
    break;
  }

  return out;
}

/**
 * Read a brief.
 *
 * @param {string} text
 * @param {Array}  materials  [{ key, name }]
 * @returns {{targets, require, exclude, bounds, unmatched,
 *            costMax, minIngredients, maxIngredients, minInclusion, optionCount}}
 */
export function parseBrief(text, materials = []) {
  const raw = String(text || '');
  const targets = {};
  const require = [];
  const exclude = [];
  const bounds = {};
  const unmatched = [];
  const consumed = [];

  const eat = (match) => consumed.push([match.index, match.index + match[0].length]);
  // Something already read for another purpose. "give me 3 options" is not an
  // energy figure of 3, and in "give me 18% protein" the 18 belongs to protein.
  const overlaps = (match) => consumed.some(([s, e]) =>
    match.index < e && match.index + match[0].length > s);

  // ── The blend as a whole ──────────────────────────────────────────────────
  // First, so "minimum 4 ingredients" is never offered to the material reader
  // as a material called "ingredients".
  const { spans, ...rules } = parseRules(raw);
  consumed.push(...spans);

  // ── Nutrients ─────────────────────────────────────────────────────────────
  // Both orders, because both get said: "18% protein" and "protein 18%".
  const nutrientAlternatives = Object.entries(NUTRIENT_WORDS)
    .flatMap(([key, words]) => words.map((w) => ({ key, word: w })))
    .sort((a, b) => b.word.length - a.word.length);

  // `\\s*` after the qualifier rather than `\\s+`: "<16% fibre" is typed as
  // often as "< 16% fibre".
  const qualifier = `(?:(${qualifierPattern([...MAX_WORDS, ...MIN_WORDS])})\\s*)?`;
  const number = '(\\d+(?:\\.\\d+)?)';

  for (const { key, word } of nutrientAlternatives) {
    if (targets[key]) continue;                       // first mention wins
    const w = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // A range first — "protein 17-19%" is one statement, not two.
    const range = new RegExp(`${number}\\s*(?:-|to|–)\\s*${number}\\s*%?\\s*${w}\\b`, 'i')
      .exec(raw) || new RegExp(`\\b${w}\\s*(?:at|of|=|:)?\\s*${number}\\s*(?:-|to|–)\\s*${number}\\s*%?`, 'i').exec(raw);
    if (range && !overlaps(range)) {
      const a = parseFloat(range[1]);
      const b = parseFloat(range[2]);
      targets[key] = { min: Math.min(a, b), max: Math.max(a, b) };
      eat(range);
      continue;
    }

    const hit =
      new RegExp(`${qualifier}${number}\\s*%?\\s*${w}\\b`, 'i').exec(raw)
      || new RegExp(`\\b${w}\\s*(?:at|of|=|:)?\\s*${qualifier}${number}\\s*%?`, 'i').exec(raw);
    if (!hit || overlaps(hit)) continue;

    const [, word1, word2] = hit;
    // The two patterns put the qualifier and the number in different groups.
    const qual = qualifierKind(word1 && Number.isNaN(Number(word1)) ? word1 : '');
    const value = parseFloat(Number.isNaN(Number(word1)) ? word2 : word1);
    if (!Number.isFinite(value)) continue;

    if (qual === 'max') targets[key] = { max: value };
    else if (qual === 'min') targets[key] = { min: value };
    // Bare "18% protein" is a floor. That is what it means on a feed label and
    // in every conversation this replaces — nobody asks for at most 18%
    // protein without saying so.
    else targets[key] = { min: value, _bare: true };
    eat(hit);
  }

  // ── Materials ─────────────────────────────────────────────────────────────
  // "no rape" / "without palm" before the include patterns, so an exclusion is
  // not read as a request for the thing being excluded.
  const excludeRe = /\b(?:no|without|avoid|exclude|not)\s+([a-z0-9][a-z0-9 &/'-]*?)(?=\s*(?:,|\.|;|$|\band\b|\bbut\b|\buse\b|\bwith\b))/gi;
  for (const m of raw.matchAll(excludeRe)) {
    for (const phrase of m[1].split(/\s*(?:,|\band\b|\bor\b)\s*/i)) {
      const hit = matchMaterial(phrase, materials);
      if (hit) { exclude.push(hit.key); eat(m); }
    }
  }

  const includeRe = /\b(?:use|using|with|include|including|containing|contains)\s+([a-z0-9][a-z0-9 &/'()-]*?)(?=\s*(?:,\s*(?:and\s+)?)?(?:\.|;|$)|\s*,\s*(?=\d)|$)/gi;
  for (const m of raw.matchAll(includeRe)) {
    for (const phrase of m[1].split(/\s*(?:,|\band\b|\bplus\b|\+)\s*/i)) {
      const clean = phrase.trim();
      if (!clean) continue;
      const hit = matchMaterial(clean, materials);
      if (hit) {
        if (!require.includes(hit.key) && !exclude.includes(hit.key)) require.push(hit.key);
        eat(m);
      } else if (clean.length > 2 && !/^\d/.test(clean)) {
        unmatched.push(clean);
      }
    }
  }

  // "max 10% rape" — a bound on a material rather than on a nutrient. Checked
  // after nutrients so "max 5% fibre" is not read as a material called fibre.
  // "< 50% wheatfeed" is the same bound written the way it gets typed.
  const boundRe = new RegExp(`(?:(${qualifierPattern(MAX_WORDS)})|(${qualifierPattern(MIN_WORDS)}))\\s*${number}\\s*%?\\s+([a-z][a-z0-9 &/'-]*?)(?=\\s*(?:,|\\.|;|$|\\band\\b))`, 'gi');
  for (const m of raw.matchAll(boundRe)) {
    const [, maxWord, , value, phrase] = m;
    if (consumed.some(([s, e]) => m.index >= s && m.index < e)) continue;
    const hit = matchMaterial(phrase, materials);
    if (!hit) continue;
    bounds[hit.key] = maxWord
      ? { ...bounds[hit.key], max: parseFloat(value) }
      : { ...bounds[hit.key], min: parseFloat(value) };
  }

  return { targets, require, exclude, bounds, unmatched, ...rules };
}

/**
 * What a material may sensibly go in at, by category.
 *
 * Least cost with no upper bounds is not a feed, it is an arithmetic result. A
 * mineral carries no protein, no fibre and no starch and costs almost nothing,
 * so the cheapest way to hit "18% protein" while diluting to 100% is to pour in
 * a third of a tonne of limestone. The LP is right; the question was wrong.
 *
 * These are the inclusion ceilings a nutritionist works to anyway — minerals a
 * couple of percent, additives under one, molasses as a coating rather than an
 * ingredient. Applied by default so a first answer is edible, reported so the
 * user can see they were applied, and overridable per material in the solver
 * for the cases that genuinely differ.
 */
export const CATEGORY_CAPS = {
  mineral: 3,
  additive: 1,
  liquid: 5
};

/** The default ceilings for a given set of materials, as solver bounds. */
export function defaultBounds(materials) {
  const out = {};
  for (const m of materials) {
    const cap = CATEGORY_CAPS[m.category];
    if (cap !== undefined) out[m.key] = { max: cap };
  }
  return out;
}

/**
 * How many answers a brief may ask for. Each is a search, and past a handful
 * they stop being meaningfully different and start being worse.
 */
export const MAX_OPTIONS = 6;

/**
 * The whole of one brief's time on the main thread, across every reading and
 * every variation. The solver has always run in the click handler; this is what
 * keeps a pathological request ("at least 15 ingredients" and nothing else) from
 * freezing the page while it proves a point nobody needs proved.
 */
export const BRIEF_TIME_BUDGET_MS = 3000;

/** At or above this share of a blend, a material is one of its main ingredients. */
export const MAIN_SHARE = 5;

/**
 * Put a brief into the one shape everything downstream reads.
 *
 * Briefs arrive from two readers — the regex parse and the AI one — and, from
 * before 1.3.0, without costMax / minIngredients / maxIngredients at all (and
 * before 1.4.0 without minInclusion). Mirrors blendBriefService.sanitise on the
 * server: keep the two in step.
 * Anything missing or malformed becomes "not asked for": never zero, which as a
 * cost ceiling would be impossible, and a count is clamped so a model that says
 * 500 is not taken at its word.
 */
export function normaliseBrief(brief) {
  const b = brief || {};
  const blank = (v) => v === null || v === undefined || v === '' || typeof v === 'boolean';
  const whole = (v, most) => {
    if (blank(v)) return null;
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? Math.min(most, n) : null;
  };
  const money = (v) => {
    if (blank(v)) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  // A percentage of the blend: positive, and no more than MAX_MIN_INCLUSION.
  const share = (v) => {
    if (blank(v)) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 && n <= MAX_MIN_INCLUSION ? n : null;
  };
  const list = (v) => (Array.isArray(v) ? v : []);
  const map = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
  return {
    ...b,
    targets: map(b.targets),
    bounds: map(b.bounds),
    require: list(b.require),
    exclude: list(b.exclude),
    unmatched: list(b.unmatched),
    // `variations` is accepted as another name for the same request.
    optionCount: whole(b.optionCount ?? b.variations, MAX_OPTIONS),
    minIngredients: whole(b.minIngredients, 60),
    maxIngredients: whole(b.maxIngredients, 60),
    // Null unless the request stated one: there is no default minimum.
    minInclusion: share(b.minInclusion),
    costMax: money(b.costMax)
  };
}

/**
 * Turn a brief into a handful of costed, proved answers.
 *
 * Each option is a different *reading* of the same request, not a different
 * algorithm — the LP is exact, so there is exactly one cheapest blend per
 * reading and nothing to be gained by trying again. What is genuinely open is
 * how literally to take the numbers, and that is what these differ on: hold
 * them exactly, allow the usual half-point of give, or spend only on what was
 * actually named.
 *
 * Infeasible readings are dropped rather than shown as failures — a brief that
 * cannot be met exactly but can be met within half a point should offer the
 * second, not report the first as an error. When NOTHING can be met, the
 * outcome says why, naming the rule that could not be kept.
 *
 * Every option keeps the brief's rules about the blend as a whole — the cost
 * ceiling and the ingredient counts. An answer that breaks one is never shown;
 * it is reported instead.
 *
 * @returns {{options, rules, requested, message, shortfall}}
 *          message   — why there are no options, when there are none
 *          shortfall — why there are fewer than the variations asked for
 */
export function buildOptionsFromBrief({
  brief: given, book, materials, columnKey, tolerance = 0.5, baseValues = null, baseName = '', limits = {}
}) {
  let brief = normaliseBrief(given);

  const deadline = limits.deadline ?? (Date.now() + (limits.ms ?? BRIEF_TIME_BUDGET_MS));
  const solveLimits = {
    deadline,
    ...(limits.nodes ? { nodes: limits.nodes } : {}),
    ...(limits.searchMs ? { ms: limits.searchMs } : {})
  };
  const rules = {
    minIngredients: brief.minIngredients,
    maxIngredients: brief.maxIngredients,
    minInclusion: brief.minInclusion,
    costMax: brief.costMax
  };
  const wanted = brief.optionCount || 0;
  const failures = [];
  const outcome = (options, extra = {}) => ({
    options, rules, requested: wanted || null, message: null, shortfall: null, ...extra
  });

  if (rules.minIngredients && rules.maxIngredients && rules.minIngredients > rules.maxIngredients) {
    return outcome([], {
      message: `At least ${rules.minIngredients} and no more than ${rules.maxIngredients} ingredients cannot both be met.`
    });
  }

  const inBook = materials.filter((m) => book.materials.has(m.key));
  const usable = inBook.filter((m) => !brief.exclude.includes(m.key));

  const named = brief.require.filter((k) => book.materials.has(k));

  /*
   * "Maintain the spec" — hold the base blend's OWN analysis.
   *
   * The figures are read off the blend rather than stated in the request, which
   * is the whole point of asking this way: "replace Rolled Beans but keep the
   * spec" means the analysis the blend already has, and nobody wants to type
   * nine numbers to say so.
   *
   * Measured, not guessed. blendMath.nutrients is the same function the
   * calculator's analysis panel uses, so the target is exactly what the screen
   * would show for that recipe. Anything the request states explicitly still
   * wins — an instruction is more specific than an inheritance.
   */
  const inheritedTargets = (() => {
    if (!brief.matchBase || !baseValues || !Object.keys(baseValues).length) return {};
    const measured = blendMath.nutrients(baseValues, book);
    // `partial` names the components some of the base's materials carry no
    // figure for. Those totals are understated, so holding one as a target
    // would chase a number the blend never actually had.
    const partial = new Set((measured.partial || []).map((p) => p.key));
    return Object.fromEntries(
      Object.entries(measured.values || {})
        .filter(([key, v]) => !partial.has(key) && Number.isFinite(Number(v)))
        .map(([key, v]) => [key, { min: Number(v), max: Number(v), _inherited: true }]));
  })();

  brief = Object.keys(inheritedTargets).length
    ? { ...brief, targets: { ...inheritedTargets, ...brief.targets } }
    : brief;

  /*
   * "Use the base blend's percentages as maximums."
   *
   * A ceiling per material, taken from the recipe rather than typed — which is
   * the whole point of asking that way. Nobody wants to write out fourteen
   * caps, and the request means "you may drop anything, and add nothing".
   *
   * Only the materials the base ACTUALLY CONTAINS are capped. Capping every
   * material in the book at its base inclusion — zero for anything absent —
   * sounds like the stricter reading and is in fact an impossible one: the caps
   * then sum to exactly 100, so the only feasible blend is the base itself and
   * every "alternative" is infeasible. Someone asking for three formulations
   * with those percentages as maximums wants substitution, not a proof that
   * their own blend is the only answer.
   *
   * So: no more wheat than it had, and you may reach for something it did not
   * have. Anything the request states explicitly still wins — an instruction is
   * more specific than an inheritance.
   */
  const baseBounds = (() => {
    if (!brief.boundsFromBase || !baseValues) return {};
    const out = {};
    for (const [key, raw] of Object.entries(baseValues)) {
      const held = Number(raw) || 0;
      if (held <= 0 || !book.materials.has(key)) continue;
      out[key] = brief.boundsFromBase === 'max' ? { max: held } : { min: held };
    }
    return out;
  })();

  brief = Object.keys(baseBounds).length
    ? { ...brief, bounds: { ...baseBounds, ...brief.bounds } }
    : brief;

  const hasTargets = Object.keys(brief.targets).length > 0;

  const clean = (targets) => Object.fromEntries(
    Object.entries(targets).map(([k, t]) => [k, { min: t.min, max: t.max }]));

  // Widen a bare figure into a band. A request for "18% protein" is met by
  // 18.0 and by 24.0, and the second costs a great deal more for something
  // nobody asked for — so the literal reading gets a ceiling too.
  const banded = (targets, give) => Object.fromEntries(
    Object.entries(targets).map(([k, t]) => {
      if (t._bare) return [k, { min: t.min, max: t.min + give }];
      // An inherited figure is a point, not a band: held to the letter it is
      // almost always infeasible once a material has been taken away, which is
      // exactly the case it exists for. It gets give either side.
      if (t._inherited) return [k, { min: t.min - give, max: t.max + give }];
      return [k, { min: t.min, max: t.max }];
    }));

  /*
   * Category ceilings first, so anything the brief stated explicitly overrides
   * them rather than the other way round. Then a floor under every named
   * material, or "use Propass" / "include wheat gluten" is satisfied by a blend
   * containing none of it.
   */
  const boundsFor = (requireNamed) => {
    const bounds = { ...defaultBounds(inBook), ...brief.bounds };
    if (requireNamed) {
      for (const key of named) {
        if (bounds[key]?.min == null) bounds[key] = { ...bounds[key], min: 1 };
      }
    }
    return bounds;
  };

  const attempts = [
    {
      key: 'exact',
      title: 'As asked',
      note: hasTargets
        ? 'Every figure held to what you said, with no more protein or starch than asked for.'
        : 'The cheapest blend from everything available.',
      candidates: usable.map((m) => m.key),
      // A quarter point of headroom, not a hundredth: the answer is rounded to
      // 2dp inclusions afterwards, and a band that tight leaves the rounding
      // nowhere to land — the blend then comes back at 17.99% against a stated
      // 18% and fails the very spec it was built for.
      targets: banded(brief.targets, 0.25),
      requireNamed: named.length > 0
    },
    {
      key: 'tolerant',
      title: `Within ${tolerance}%`,
      note: `The same figures with ${tolerance}% of give either way — usually the cheaper blend, and inside what a feed label allows.`,
      candidates: usable.map((m) => m.key),
      targets: banded(brief.targets, tolerance),
      requireNamed: named.length > 0,
      // With no figures to give on, this is "As asked" again — the same answer,
      // and with ingredient rules the same search, paid for twice.
      skip: !hasTargets
    },
    {
      key: 'named-only',
      title: 'Only what you named',
      note: 'Built from your materials alone, with nothing else brought in.',
      candidates: named,
      targets: banded(brief.targets, tolerance),
      requireNamed: false,
      skip: named.length < 2
    },
    {
      key: 'open',
      title: 'Anything goes',
      note: 'The cheapest blend that meets the spec, ignoring which materials you named.',
      candidates: usable.map((m) => m.key),
      targets: banded(brief.targets, tolerance),
      requireNamed: false,
      skip: named.length === 0
    }
  ];

  // ── Starting from an existing blend ─────────────────────────────────────
  // "Take X and make it 18% protein" is a request for X, moved as little as
  // possible — not for the cheapest 18% blend, which is a different recipe the
  // customer would not recognise. When a base is named it leads the list.
  const results = [];
  const seen = new Set();

  if (baseValues && Object.keys(baseValues).length) {
    const usableKeys = usable.map((m) => m.key);
    const baseAttempts = [
      { key: 'closest', title: baseName ? `${baseName}, barely changed` : 'Barely changed',
        note: 'The smallest possible change to the blend you named — only what the spec forces.',
        /*
         * The base's own materials, MINUS anything excluded.
         *
         * Without that filter this attempt keeps a banned material: it starts
         * from the base's recipe and minimises movement, so a material already
         * in the blend at 25% stays at 25% because moving it is the change it
         * is trying to avoid. "Take X but no rape" came back with the rape
         * still in it — the one instruction that was given.
         */
        candidates: [...new Set([...Object.keys(baseValues), ...named])]
          .filter((k) => book.materials.has(k) && !brief.exclude.includes(k)) },
      { key: 'closest-open', title: baseName ? `${baseName}, any material` : 'Smallest change, any material',
        note: 'The smallest change again, but allowed to reach for materials the blend does not currently use.',
        candidates: usableKeys }
    ];

    for (const attempt of baseAttempts) {
      if (!attempt.candidates.length) continue;
      const solved = solveClosestToBase({
        book,
        baseValues,
        candidates: attempt.candidates,
        columnKey,
        spec: hasTargets ? { targets: clean(banded(brief.targets, tolerance)) } : null,
        bounds: boundsFor(named.length > 0),
        ...rules,
        limits: solveLimits
      });
      if (solved.status !== 'optimal') {
        failures.push({ key: attempt.key, title: attempt.title, status: solved.status, message: solved.message });
        continue;
      }

      const signature = signatureOf(solved.values);
      if (seen.has(signature)) continue;
      seen.add(signature);

      results.push({
        ...attempt,
        fromBase: true,
        movedTotal: solved.movedTotal,
        values: solved.values,
        cost: solved.cost,
        excluded: solved.excluded,
        binding: [],
        capped: [],
        ingredients: solved.ingredients,
        proved: solved.proved,
        nutrients: blendMath.nutrients(solved.values, book).values
      });
    }
  }

  for (const attempt of attempts) {
    if (attempt.skip || !attempt.candidates.length) continue;

    const values = Object.fromEntries(attempt.candidates.map((k) => [k, 0]));
    const solved = solveBlend({
      book,
      values,
      locked: [],
      candidates: attempt.candidates,
      columnKey,
      spec: Object.keys(attempt.targets).length ? { targets: clean(attempt.targets) } : null,
      bounds: boundsFor(attempt.requireNamed),
      // The candidate set here is the whole cost book, so anything the solver
      // did not use must not land in the grid at 0 — a suggestion should arrive
      // as the handful of materials it actually uses.
      dropZeros: true,
      ...rules,
      limits: solveLimits
    });

    if (solved.status !== 'optimal') {
      failures.push({ key: attempt.key, title: attempt.title, status: solved.status, message: solved.message });
      continue;
    }

    // Two readings often land on the same blend — show it once, under the
    // first (most literal) heading that produced it.
    const signature = signatureOf(solved.values);
    if (seen.has(signature)) continue;
    seen.add(signature);

    results.push({
      ...attempt,
      capped: Object.keys(defaultBounds(inBook))
        .filter((k) => isIncluded(solved.values[k]) && brief.bounds[k] === undefined),
      values: solved.values,
      cost: solved.cost,
      excluded: solved.excluded,
      binding: solved.binding,
      ingredients: solved.ingredients,
      proved: solved.proved,
      searchGap: solved.searchGap,
      nutrients: blendMath.nutrients(solved.values, book).values
    });
  }

  // Base-derived options first, least-moved first within them; then the
  // built-from-scratch ones by cost. Someone who named a starting blend wants
  // to see what happened to it before they see alternatives to it.
  const ordered = results.sort((a, b) => {
    if (!!b.fromBase !== !!a.fromBase) return a.fromBase ? -1 : 1;
    if (a.fromBase) return (a.movedTotal || 0) - (b.movedTotal || 0);
    return a.cost - b.cost;
  });

  /*
   * The last word on the rules, whatever produced the answer.
   *
   * The solvers keep them by construction; this is here for what they do not
   * control — a base blend's unpriced material restored at its own figure, say,
   * which adds an ingredient the search never saw. A blend that breaks the
   * brief is not an option, however it came about.
   */
  const breaksRules = (option) => {
    const count = countIngredients(option.values);
    if (rules.minIngredients && count < rules.minIngredients) return `has only ${count} ingredients`;
    if (rules.maxIngredients && count > rules.maxIngredients) return `has ${count} ingredients`;
    if (rules.costMax && option.cost > rules.costMax + 0.01) return `costs £${option.cost.toFixed(2)}/T`;
    if (rules.minInclusion) {
      // Within half a step: a stated 0.024% comes back rounded to 0.02%.
      const under = Object.entries(option.values || {})
        .find(([, v]) => isIncluded(v) && Number(v) < rules.minInclusion - HALF_STEP);
      if (under) {
        const name = book.materials.get(under[0])?.name || under[0];
        return `has ${name} at ${Number(under[1]).toFixed(2)}%, under the ${formatInclusion(rules.minInclusion)}% minimum`;
      }
    }
    if (brief.exclude.some((k) => isIncluded(option.values?.[k]))) return 'uses a material you excluded';
    return null;
  };
  const kept = ordered.filter((option) => {
    const why = breaksRules(option);
    if (why) {
      failures.push({ key: option.key, title: option.title, status: 'broke-rule',
                      message: `${option.title} ${why}, which the brief rules out.` });
    }
    return !why;
  });

  /** Why nothing came back, in the terms of the rule that stopped it. */
  const failureMessage = () => {
    const primary = ['exact', 'tolerant', 'closest', 'closest-open']
      .map((k) => failures.find((f) => f.key === k))
      .find(Boolean) || failures[0];
    if (!primary) return null;
    // When the ceiling is what is in the way, say what the blend would cost
    // without it — "£263.40/T" is an answer; "infeasible" is not.
    if (rules.costMax && primary.status === 'infeasible' && Date.now() < deadline) {
      const relaxed = solveBlend({
        book,
        candidates: usable.map((m) => m.key),
        columnKey,
        spec: hasTargets ? { targets: clean(banded(brief.targets, tolerance)) } : null,
        bounds: boundsFor(named.length > 0),
        dropZeros: true,
        ...rules,
        costMax: null,
        limits: solveLimits
      });
      if (relaxed.status === 'optimal' && relaxed.cost > rules.costMax) {
        return `The cheapest blend that meets everything else costs £${relaxed.cost.toFixed(2)}/T — `
          + `over your £${rules.costMax.toFixed(2)}/T ceiling.`;
      }
    }
    return primary.message || null;
  };

  if (!kept.length) return outcome([], { message: failureMessage() });
  if (!wanted) return outcome(kept);

  /*
   * "Give me three options" / "three variations".
   *
   * N genuinely different blends, every one keeping every rule. The readings
   * above count where they genuinely differ — but "As asked" and "Within 0.5%"
   * usually land on the same materials a few hundredths apart, and that is the
   * same blend twice, not two variations.
   *
   * GENUINELY DIFFERENT means: leaves out at least one MAIN ingredient (5% or
   * more) of every blend before it. Materials the brief itself requires — named,
   * or given a minimum — do not count, since no variation may drop them. The
   * shortfall is then made up by searching for exactly that: the cheapest blend
   * that keeps the brief and omits one main ingredient of each option so far. It
   * is a proved answer to a stated question, not "ban the biggest and see",
   * which used to drop the named materials along with it.
   *
   * "Anything goes" is not a variation when materials were named: it exists to
   * show what naming them costs, and it ignores them to do it.
   */
  const forcedIn = new Set([
    ...named,
    ...Object.entries(brief.bounds)
      .filter(([, b]) => (blendMath.toNumber(b?.min) || 0) > 0)
      .map(([k]) => k)
  ]);
  const present = (values, key) => isIncluded(values?.[key]);
  const mainOf = (values) => {
    const inBlend = Object.keys(values || {}).filter((k) => present(values, k) && !forcedIn.has(k));
    const main = inBlend.filter((k) => Number(values[k]) >= MAIN_SHARE);
    return main.length ? main : inBlend;
  };
  const differs = (later, earlier) => {
    const main = mainOf(earlier.values);
    if (!main.length) return signatureOf(later.values) !== signatureOf(earlier.values);
    return main.some((k) => !present(later.values, k));
  };

  const accepted = [];
  for (const option of kept) {
    if (accepted.length >= wanted) break;
    if (option.key === 'open' && named.length) continue;
    if (accepted.every((earlier) => differs(option, earlier))) accepted.push(option);
  }

  const nameOf = (k) => book.materials.get(k)?.name || k;
  const listOf = (keys) => {
    const names = keys.map(nameOf);
    return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  };

  let stoppedBy = null;
  const candidates = usable.map((m) => m.key);
  for (let round = 1; accepted.length && accepted.length < wanted && round <= MAX_OPTIONS; round += 1) {
    if (Date.now() > deadline) { stoppedBy = 'Ran out of time looking for more.'; break; }
    const cuts = accepted.map((a) => mainOf(a.values));
    if (cuts.some((cut) => !cut.length)) {
      stoppedBy = 'Everything in the blends so far is something you asked for, so there is nothing left to vary.';
      break;
    }

    const solved = solveBlend({
      book,
      candidates,
      columnKey,
      spec: hasTargets ? { targets: clean(banded(brief.targets, tolerance)) } : null,
      bounds: boundsFor(named.length > 0),
      // As the attempts above: a suggestion arrives as the handful of materials
      // it actually uses, not the whole book with forty zeros in it.
      dropZeros: true,
      ...rules,
      differentFrom: cuts,
      limits: solveLimits
    });
    if (solved.status !== 'optimal') { stoppedBy = solved.message; break; }
    if (breaksRules(solved)) { stoppedBy = 'No other blend keeps every rule.'; break; }

    const previous = accepted[accepted.length - 1];
    const dropped = mainOf(previous.values).filter((k) => !present(solved.values, k));
    const broughtIn = Object.keys(solved.values)
      .filter((k) => present(solved.values, k) && !accepted.some((a) => present(a.values, k)));

    accepted.push({
      ...solved,
      key: `variation-${round}`,
      title: dropped.length ? `Without ${listOf(dropped.slice(0, 2))}${dropped.length > 2 ? ` and ${dropped.length - 2} more` : ''}`
        : `Variation ${accepted.length + 1}`,
      note: `The same brief reached another way${broughtIn.length ? ` — brings in ${listOf(broughtIn)}` : ''}. `
        + 'Leaves out at least one main ingredient of each blend above it.',
      variation: true,
      binding: solved.binding || [],
      capped: [],
      nutrients: blendMath.nutrients(solved.values, book).values
    });
  }

  return outcome(accepted.slice(0, wanted), {
    shortfall: accepted.length < wanted
      ? `Found ${accepted.length} of the ${wanted} different blends asked for. ${stoppedBy || ''}`.trim()
      : null
  });
}

/**
 * The options alone — the shape every caller used before the outcome carried
 * its reasons.
 */
export function buildFromBrief(args) {
  return buildOptionsFromBrief(args).options;
}

/** Two recipes are the same answer when the same materials hold the same figures. */
function signatureOf(values) {
  return Object.entries(values || {})
    .filter(([, v]) => isIncluded(v))
    .map(([k, v]) => `${k}:${Number(v).toFixed(INCLUSION_DECIMALS)}`)
    .sort()
    .join('|');
}

/** Does this text look like a brief rather than a command name? */
export function looksLikeBrief(text, catalogue = null) {
  const t = String(text || '');
  if (t.trim().length < 8) return false;
  if (/\d\s*%/.test(t)) return true;
  if (/\b(?:use|using|with|without|no more than|at least|make it|take|based on|like)\b/i.test(t)) return true;
  if (Object.values(NUTRIENT_WORDS).flat().some((w) => new RegExp(`\\b${w}\\b`, 'i').test(t))) return true;
  // "minimum 4 ingredients maximum 7" has no figure with a % and no nutrient,
  // and is still a brief.
  const rules = parseRules(t);
  if (rules.costMax || rules.minIngredients || rules.maxIngredients || rules.minInclusion || rules.optionCount) return true;
  // A sentence that names a blend, a formulation or a customer is a brief even
  // without a figure in it — "something like Chapple's November blend" is a
  // request the builder can act on, and the palette offering it straight away
  // saves a trip through a second screen.
  if (catalogue) {
    const refs = findReferences(t, catalogue);
    if (refs.base || refs.baseCandidates.length || refs.customer) return true;
  }
  return false;
}

export { NUTRIENT_WORDS };
